import { useState } from 'react';
import {
  Plus,
  Search,
  Edit3,
  Trash2,
  Eye,
  EyeOff,
  X,
  Image,
  DollarSign,
  Tag,
} from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

// Mock menu data for the cafe
const menuCategories = [
  { id: 1, name: { ar: 'قهوة ساخنة', en: 'Hot Coffee' }, icon: '☕', itemCount: 8 },
  { id: 2, name: { ar: 'قهوة باردة', en: 'Iced Coffee' }, icon: '🧊', itemCount: 6 },
  { id: 3, name: { ar: 'حلويات', en: 'Desserts' }, icon: '🍰', itemCount: 5 },
  { id: 4, name: { ar: 'مخبوزات', en: 'Bakery' }, icon: '🥐', itemCount: 4 },
  { id: 5, name: { ar: 'عصائر', en: 'Juices' }, icon: '🥤', itemCount: 3 },
  { id: 6, name: { ar: 'وجبات خفيفة', en: 'Snacks' }, icon: '🥪', itemCount: 3 },
];

interface MenuItem {
  id: number;
  name: { ar: string; en: string };
  description: { ar: string; en: string };
  price: number;
  categoryId: number;
  image: string;
  isAvailable: boolean;
  isPopular: boolean;
  preparationTime: number;
  calories?: number;
}

const menuItems: MenuItem[] = [
  { id: 1, name: { ar: 'كابتشينو', en: 'Cappuccino' }, description: { ar: 'كابتشينو كلاسيكي بالرغوة الكريمية', en: 'Classic cappuccino with creamy foam' }, price: 45, categoryId: 1, image: '☕', isAvailable: true, isPopular: true, preparationTime: 5, calories: 120 },
  { id: 2, name: { ar: 'لاتيه', en: 'Latte' }, description: { ar: 'لاتيه ناعم مع حليب مبخر', en: 'Smooth latte with steamed milk' }, price: 50, categoryId: 1, image: '☕', isAvailable: true, isPopular: true, preparationTime: 5, calories: 150 },
  { id: 3, name: { ar: 'إسبريسو', en: 'Espresso' }, description: { ar: 'إسبريسو مركز وغني', en: 'Rich concentrated espresso' }, price: 30, categoryId: 1, image: '☕', isAvailable: true, isPopular: false, preparationTime: 3, calories: 5 },
  { id: 4, name: { ar: 'موكا', en: 'Mocha' }, description: { ar: 'قهوة بالشوكولاتة الفاخرة', en: 'Premium chocolate coffee' }, price: 55, categoryId: 1, image: '☕', isAvailable: true, isPopular: true, preparationTime: 6, calories: 200 },
  { id: 5, name: { ar: 'أمريكانو', en: 'Americano' }, description: { ar: 'قهوة أمريكية سوداء', en: 'Black American coffee' }, price: 35, categoryId: 1, image: '☕', isAvailable: true, isPopular: false, preparationTime: 3, calories: 10 },
  { id: 6, name: { ar: 'قهوة تركي', en: 'Turkish Coffee' }, description: { ar: 'قهوة تركية تقليدية', en: 'Traditional Turkish coffee' }, price: 25, categoryId: 1, image: '☕', isAvailable: false, isPopular: false, preparationTime: 8, calories: 15 },
  { id: 7, name: { ar: 'آيس لاتيه', en: 'Iced Latte' }, description: { ar: 'لاتيه مثلج منعش', en: 'Refreshing iced latte' }, price: 55, categoryId: 2, image: '🧊', isAvailable: true, isPopular: true, preparationTime: 4, calories: 160 },
  { id: 8, name: { ar: 'فرابتشينو', en: 'Frappuccino' }, description: { ar: 'فرابتشينو كريمي مع الثلج', en: 'Creamy frappuccino with ice' }, price: 65, categoryId: 2, image: '🧊', isAvailable: true, isPopular: true, preparationTime: 5, calories: 280 },
  { id: 9, name: { ar: 'آيس موكا', en: 'Iced Mocha' }, description: { ar: 'موكا مثلجة بالشوكولاتة', en: 'Iced mocha with chocolate' }, price: 60, categoryId: 2, image: '🧊', isAvailable: true, isPopular: false, preparationTime: 5, calories: 250 },
  { id: 10, name: { ar: 'تشيز كيك', en: 'Cheesecake' }, description: { ar: 'تشيز كيك كلاسيكي', en: 'Classic cheesecake' }, price: 55, categoryId: 3, image: '🍰', isAvailable: true, isPopular: true, preparationTime: 2, calories: 350 },
  { id: 11, name: { ar: 'تيراميسو', en: 'Tiramisu' }, description: { ar: 'تيراميسو إيطالي أصلي', en: 'Authentic Italian tiramisu' }, price: 60, categoryId: 3, image: '🍰', isAvailable: true, isPopular: false, preparationTime: 2, calories: 400 },
  { id: 12, name: { ar: 'كيك شوكولاتة', en: 'Chocolate Cake' }, description: { ar: 'كيك شوكولاتة غنية', en: 'Rich chocolate cake' }, price: 50, categoryId: 3, image: '🍰', isAvailable: true, isPopular: false, preparationTime: 2, calories: 380 },
  { id: 13, name: { ar: 'كرواسون', en: 'Croissant' }, description: { ar: 'كرواسون طازج بالزبدة', en: 'Fresh butter croissant' }, price: 35, categoryId: 4, image: '🥐', isAvailable: true, isPopular: true, preparationTime: 3, calories: 230 },
  { id: 14, name: { ar: 'بان كيك', en: 'Pancakes' }, description: { ar: 'بان كيك رقيق مع القيقب', en: 'Thin pancakes with maple syrup' }, price: 45, categoryId: 4, image: '🥐', isAvailable: true, isPopular: false, preparationTime: 8, calories: 300 },
  { id: 15, name: { ar: 'وافل', en: 'Waffle' }, description: { ar: 'وافل بلجيكي ساخن', en: 'Hot Belgian waffle' }, price: 50, categoryId: 4, image: '🥐', isAvailable: true, isPopular: false, preparationTime: 7, calories: 320 },
  { id: 16, name: { ar: 'عصير برتقال', en: 'Orange Juice' }, description: { ar: 'عصير برتقال طبيعي طازج', en: 'Fresh natural orange juice' }, price: 30, categoryId: 5, image: '🥤', isAvailable: true, isPopular: false, preparationTime: 3, calories: 110 },
  { id: 17, name: { ar: 'عصير مانجو', en: 'Mango Juice' }, description: { ar: 'عصير مانجو كثيف', en: 'Thick mango juice' }, price: 35, categoryId: 5, image: '🥤', isAvailable: true, isPopular: true, preparationTime: 3, calories: 130 },
  { id: 18, name: { ar: 'سلطة سيزر', en: 'Caesar Salad' }, description: { ar: 'سلطة سيزر كلاسيكية', en: 'Classic Caesar salad' }, price: 65, categoryId: 6, image: '🥪', isAvailable: true, isPopular: false, preparationTime: 5, calories: 200 },
  { id: 19, name: { ar: 'ساندويتش كلوب', en: 'Club Sandwich' }, description: { ar: 'ساندويتش كلوب تريبل', en: 'Triple club sandwich' }, price: 75, categoryId: 6, image: '🥪', isAvailable: true, isPopular: false, preparationTime: 8, calories: 450 },
];

export default function CafeMenu() {
  const { formatCurrency, language } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [items, setItems] = useState(menuItems);

  const filteredItems = items.filter((item) => {
    const matchesCategory = !selectedCategory || item.categoryId === selectedCategory;
    const matchesSearch = !searchQuery ||
      item.name.ar.includes(searchQuery) ||
      item.name.en.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const availableCount = items.filter((i) => i.isAvailable).length;
  const popularCount = items.filter((i) => i.isPopular).length;

  const toggleAvailability = (id: number) => {
    setItems((prev) => prev.map((item) => item.id === id ? { ...item, isAvailable: !item.isAvailable } : item));
  };

  const togglePopular = (id: number) => {
    setItems((prev) => prev.map((item) => item.id === id ? { ...item, isPopular: !item.isPopular } : item));
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
            {language === 'ar' ? 'قائمة الطعام' : 'Menu Management'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {language === 'ar'
              ? `${items.length} ${language === 'ar' ? 'عنصر' : 'items'} — ${availableCount} ${language === 'ar' ? 'متاح' : 'available'}`
              : `${items.length} items — ${availableCount} available`}
          </p>
        </div>
        <button
          onClick={() => { setEditingItem(null); setShowAddModal(true); }}
          className="px-4 py-2.5 rounded-xl bg-amber-600 text-white font-semibold hover:bg-amber-700 transition-colors flex items-center gap-2 shadow-lg shadow-amber-200 dark:shadow-amber-900/30"
        >
          <Plus size={18} />
          {language === 'ar' ? 'إضافة عنصر' : 'Add Item'}
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <p className="text-2xl font-bold text-slate-800 dark:text-white">{items.length}</p>
          <p className="text-xs text-slate-500">{language === 'ar' ? 'إجمالي العناصر' : 'Total Items'}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">{availableCount}</p>
          <p className="text-xs text-slate-500">{language === 'ar' ? 'متاح' : 'Available'}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{popularCount}</p>
          <p className="text-xs text-slate-500">{language === 'ar' ? 'الأكثر مبيعاً' : 'Popular'}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4 text-center">
          <p className="text-2xl font-bold text-slate-800 dark:text-white">{menuCategories.length}</p>
          <p className="text-xs text-slate-500">{language === 'ar' ? 'التصنيفات' : 'Categories'}</p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
            !selectedCategory
              ? 'bg-amber-600 text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-amber-300'
          }`}
        >
          {language === 'ar' ? 'الكل' : 'All'} ({items.length})
        </button>
        {menuCategories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${
              selectedCategory === cat.id
                ? 'bg-amber-600 text-white shadow-lg'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-amber-300'
            }`}
          >
            <span>{cat.icon}</span>
            {cat.name[language]}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute start-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder={language === 'ar' ? 'بحث في القائمة...' : 'Search menu...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full ps-11 pe-4 py-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
        />
      </div>

      {/* Menu Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredItems.map((item) => {
          const category = menuCategories.find((c) => c.id === item.categoryId);
          return (
            <div
              key={item.id}
              className={`bg-white dark:bg-slate-800 rounded-2xl border overflow-hidden transition-all hover:shadow-lg ${
                item.isAvailable
                  ? 'border-slate-200 dark:border-slate-700'
                  : 'border-red-200 dark:border-red-800 opacity-75'
              }`}
            >
              {/* Item Image / Emoji */}
              <div className="h-32 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-slate-700 dark:to-slate-800 flex items-center justify-center relative">
                <span className="text-5xl">{item.image}</span>
                {item.isPopular && (
                  <span className="absolute top-3 start-3 px-2 py-0.5 rounded-lg bg-amber-500 text-white text-xs font-bold">
                    ⭐ {language === 'ar' ? 'مميز' : 'Popular'}
                  </span>
                )}
                {!item.isAvailable && (
                  <div className="absolute inset-0 bg-slate-900/50 flex items-center justify-center">
                    <span className="px-3 py-1 rounded-lg bg-red-600 text-white text-sm font-bold">
                      {language === 'ar' ? 'غير متاح' : 'Unavailable'}
                    </span>
                  </div>
                )}
                <div className="absolute top-3 end-3 flex gap-1">
                  <button
                    onClick={() => togglePopular(item.id)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      item.isPopular
                        ? 'bg-amber-500 text-white'
                        : 'bg-white/80 text-slate-400 hover:text-amber-500'
                    }`}
                    title={language === 'ar' ? 'مميز' : 'Popular'}
                  >
                    <Tag size={14} />
                  </button>
                </div>
              </div>

              {/* Item Details */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-1">
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-white">
                      {item.name[language]}
                    </h3>
                    <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                      {category?.icon} {category?.name[language]}
                    </p>
                  </div>
                  <span className="text-lg font-bold text-amber-600 dark:text-amber-400">
                    {formatCurrency(item.price)}
                  </span>
                </div>

                <p className="text-sm text-slate-500 dark:text-slate-400 mt-2 line-clamp-2">
                  {item.description[language]}
                </p>

                <div className="flex items-center gap-3 mt-3 text-xs text-slate-400">
                  <span>⏱️ {item.preparationTime} {language === 'ar' ? 'دقيقة' : 'min'}</span>
                  {item.calories && <span>🔥 {item.calories} cal</span>}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100 dark:border-slate-700">
                  <button
                    onClick={() => toggleAvailability(item.id)}
                    className={`flex-1 px-3 py-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-1 transition-colors ${
                      item.isAvailable
                        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200'
                    }`}
                  >
                    {item.isAvailable ? <Eye size={14} /> : <EyeOff size={14} />}
                    {item.isAvailable
                      ? (language === 'ar' ? 'متاح' : 'Available')
                      : (language === 'ar' ? 'مخفي' : 'Hidden')}
                  </button>
                  <button
                    onClick={() => { setEditingItem(item); setShowAddModal(true); }}
                    className="p-2 rounded-xl bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 transition-colors"
                  >
                    <Edit3 size={16} />
                  </button>
                  <button className="p-2 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredItems.length === 0 && (
        <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
          <Search size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
          <p className="text-lg font-semibold text-slate-500">
            {language === 'ar' ? 'لا توجد عناصر' : 'No items found'}
          </p>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAddModal(false)}>
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-800 dark:text-white">
                {editingItem
                  ? (language === 'ar' ? 'تعديل عنصر' : 'Edit Item')
                  : (language === 'ar' ? 'إضافة عنصر جديد' : 'Add New Item')}
              </h2>
              <button onClick={() => setShowAddModal(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700">
                <X size={20} className="text-slate-400" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Image */}
              <div className="flex justify-center">
                <div className="w-24 h-24 rounded-2xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center cursor-pointer hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">
                  {editingItem ? (
                    <span className="text-4xl">{editingItem.image}</span>
                  ) : (
                    <Image size={32} className="text-slate-400" />
                  )}
                </div>
              </div>

              {/* Name AR */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  {language === 'ar' ? 'الاسم بالعربي' : 'Name (Arabic)'}
                </label>
                <input
                  type="text"
                  defaultValue={editingItem?.name.ar || ''}
                  placeholder="كابتشينو"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                  dir="rtl"
                />
              </div>

              {/* Name EN */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  {language === 'ar' ? 'الاسم بالإنجليزي' : 'Name (English)'}
                </label>
                <input
                  type="text"
                  defaultValue={editingItem?.name.en || ''}
                  placeholder="Cappuccino"
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                  dir="ltr"
                />
              </div>

              {/* Description AR */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  {language === 'ar' ? 'الوصف بالعربي' : 'Description (Arabic)'}
                </label>
                <textarea
                  defaultValue={editingItem?.description.ar || ''}
                  rows={2}
                  className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none resize-none"
                  dir="rtl"
                />
              </div>

              {/* Price & Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                    <DollarSign size={14} className="inline" /> {language === 'ar' ? 'السعر' : 'Price'}
                  </label>
                  <input
                    type="number"
                    defaultValue={editingItem?.price || ''}
                    placeholder="0"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                    <Tag size={14} className="inline" /> {language === 'ar' ? 'التصنيف' : 'Category'}
                  </label>
                  <select
                    defaultValue={editingItem?.categoryId || ''}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                  >
                    <option value="">{language === 'ar' ? 'اختر التصنيف' : 'Select category'}</option>
                    {menuCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name[language]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Prep Time & Calories */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                    ⏱️ {language === 'ar' ? 'وقت التحضير (دقيقة)' : 'Prep Time (min)'}
                  </label>
                  <input
                    type="number"
                    defaultValue={editingItem?.preparationTime || ''}
                    placeholder="5"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                    🔥 {language === 'ar' ? 'السعرات الحرارية' : 'Calories'}
                  </label>
                  <input
                    type="number"
                    defaultValue={editingItem?.calories || ''}
                    placeholder="0"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex gap-3">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 transition-colors"
              >
                {editingItem
                  ? (language === 'ar' ? 'حفظ التعديلات' : 'Save Changes')
                  : (language === 'ar' ? 'إضافة العنصر' : 'Add Item')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
